import { ChatMessages } from "@storytell/cp-client";
import {
  getOperationLastDeltaTimestamp,
  isProgressOperation,
  type StreamingChatId,
  StreamingChatStatus,
  StreamingMessageStatus,
  type StreamingOperation,
  type StreamingOperationId,
} from "@storytell/streaming";
import { TbLoader } from "solid-icons/tb";
import { createEffect, createMemo, createSignal, Index, onCleanup, Show, untrack } from "solid-js";
import { type HeartbeatStatus, heartbeatStatus, sameHeartbeatStatus } from "../message/runningSteps";
import { useActivityFocus } from "../runtime/ActivityFocusContext";
import { useChatRuntime } from "../runtime/ChatRuntime";

/** The bouncing-dots pulse, exported for hosts' own pre-stream placeholders. */
export const HeartbeatDots = () => {
  const bounceKeyframes = `
      @keyframes bounceDot {
        0%, 100% {
          transform: translateY(0);
          animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
        }
        50% {
          transform: translateY(-4px);
          animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
        }
      }
    `;

  return (
    <>
      <style>{bounceKeyframes}</style>
      <div class="flex items-center gap-1" style={{ contain: "layout style" }}>
        <div
          class="size-1 rounded-full bg-secondary-text"
          style={{
            animation: "bounceDot 0.6s infinite",
            "animation-delay": "0s",
            "will-change": "transform",
          }}
        />
        <div
          class="size-1 rounded-full bg-secondary-text"
          style={{
            animation: "bounceDot 0.6s infinite",
            "animation-delay": "0.1s",
            "will-change": "transform",
          }}
        />
        <div
          class="size-1 rounded-full bg-secondary-text"
          style={{
            animation: "bounceDot 0.6s infinite",
            "animation-delay": "0.2s",
            "will-change": "transform",
          }}
        />
      </div>
    </>
  );
};

export const HeartbeatIndicator = (props: { chatId: StreamingChatId }) => {
  const rt = useChatRuntime();
  const t = rt.t;
  const activityFocus = useActivityFocus();

  const [shouldRender, setShouldRender] = createSignal(false);
  const [isAnimatingOut, setIsAnimatingOut] = createSignal(false);
  const [staleCheckTrigger, setStaleCheckTrigger] = createSignal(0);
  const [showReconnecting, setShowReconnecting] = createSignal(false);

  const isReconnecting = () => rt.ui?.isReconnecting?.() ?? false;
  const isChatStreaming = () => rt.store.chatStatus?.(props.chatId) === StreamingChatStatus.GENERATING;

  // Combined heartbeat state - single scan for all data
  const heartbeatState = createMemo(() => {
    // Trigger re-computation on timer (for stale detection)
    staleCheckTrigger();

    const result = {
      hasActiveHeartbeat: false,
      hasOtherStreamingOperations: false,
      operations: [] as StreamingOperation[],
    };

    if (!props.chatId) {
      return result;
    }

    const chat = rt.store.chat(props.chatId);
    if (!chat?.messages) {
      return result;
    }

    // Only a turn still generating can hold a running or progress operation; scanning every
    // message of a long thread (and every operation in the store) on each run was the cost.
    const generating = new Set<string>();
    const allOperationIds = new Set<string>();
    for (const messageId of chat.messages) {
      const message = rt.store.message(messageId);
      if (message?.status !== StreamingMessageStatus.GENERATING) continue;
      generating.add(messageId);
      for (const opId of message.operations ?? []) allOperationIds.add(opId);
    }
    if (generating.size === 0) return result;

    // No sweep of the whole operation store: an operation is linked into its message as soon as
    // the message exists, so a generating message already lists all of its operations. The sweep
    // read every operation the session ever saw, on every operation event.
    for (const operationId of allOperationIds) {
      const operation = rt.store.operation(operationId);
      if (!operation) {
        continue;
      }
      result.operations.push(operation);

      if (!operation.isComplete && isProgressOperation(operation.activity)) {
        result.hasActiveHeartbeat = true;
        continue; // Progress operations don't count as "other streaming"
      }

      // Skip nested operations - they're part of parent agent/task UI
      if (operation.parentOperationId) {
        continue;
      }

      // Skip agent/task operations - they have their own dedicated UI
      const isAgentOrTaskOperation =
        operation.activity?.startsWith("agent_") ||
        operation.activity?.toLowerCase().includes("progress") ||
        operation.name?.toLowerCase().includes("progress") ||
        operation.name?.startsWith("Agent:");
      if (isAgentOrTaskOperation) {
        continue;
      }

      if (!operation.isComplete || operation.status === ChatMessages.OperationStatus.RUNNING) {
        // Untracked: the delta clock ticks every frame while text streams, and staleness only
        // needs the 1.5s check below.
        const lastDelta = untrack(getOperationLastDeltaTimestamp(operationId as StreamingOperationId));
        const isStale =
          lastDelta === 0
            ? operation.startedAt && Date.now() - operation.startedAt.getTime() > 1000
            : Date.now() - lastDelta > 1000;

        if (!isStale) {
          result.hasOtherStreamingOperations = true;
        }
      }
    }

    return result;
  });

  const hasActiveHeartbeat = () => heartbeatState().hasActiveHeartbeat;
  const hasOtherStreamingOperations = () => heartbeatState().hasOtherStreamingOperations;
  // Equal statuses keep the old value, so the agent rows aren't rebuilt on every stream event.
  const status = createMemo(
    () => {
      const state = heartbeatState();
      return state.hasActiveHeartbeat ? heartbeatStatus(state.operations, t, (id) => rt.store.block(id)) : undefined;
    },
    undefined,
    { equals: sameHeartbeatStatus }
  );
  // Holds the last status through the fade-out, which starts once the status is gone.
  const shown = createMemo((prev: HeartbeatStatus) => status() ?? prev, { main: "", agents: [] });

  createEffect(() => {
    const isActive = hasActiveHeartbeat() && status() !== undefined;
    const hasOtherStreaming = hasOtherStreamingOperations();
    const reconnecting = isReconnecting();
    const isStreaming = isChatStreaming();

    if (reconnecting) {
      setShowReconnecting(true);
      setShouldRender(true);
      setIsAnimatingOut(false);
      return;
    }

    if (showReconnecting()) {
      setShowReconnecting(false);
      // If chat is no longer streaming after reconnecting ends (e.g., stream
      // completed while offline), animate out the indicator
      if (!isChatStreaming() && shouldRender()) {
        setIsAnimatingOut(true);
        setTimeout(() => {
          setShouldRender(false);
          setIsAnimatingOut(false);
        }, 300);
        return;
      }
    }

    if (!isStreaming && !isActive && shouldRender()) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setShouldRender(false);
        setIsAnimatingOut(false);
      }, 300);
      return;
    }

    if (isActive && !hasOtherStreaming) {
      setShouldRender(true);
      setIsAnimatingOut(false);
    } else if (shouldRender()) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setShouldRender(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  });

  createEffect(() => {
    const isStreaming = isChatStreaming();
    if (!isStreaming) return;

    const interval = setInterval(() => {
      setStaleCheckTrigger((prev) => prev + 1);
    }, 1500);

    onCleanup(() => clearInterval(interval));
  });

  return (
    <Show when={shouldRender()}>
      <output
        aria-live="polite"
        aria-label={t("aria_label_processing_indicator")}
        // Same rhythm as the notes and answer prose around it, and no inset: its edge is the column's.
        class="block pt-1 pb-3"
        classList={{
          "animate-in fade-in duration-500": !isAnimatingOut(),
          "animate-out fade-out duration-300": isAnimatingOut(),
        }}
      >
        <button
          type="button"
          title={t("chat_scroll_to_bottom_autoscroll")}
          onClick={() => rt.actions.resumeAutoscroll?.()}
          class="flex max-w-full cursor-pointer items-center gap-2 text-left text-sm"
          classList={{
            // The padding is taken back by the margin, so the text keeps the column's edge.
            "-mx-2 px-2 py-1 rounded-md bg-amber-100/80 dark:bg-amber-900/50 hover:bg-amber-200/80 dark:hover:bg-amber-800/50 text-amber-700 dark:text-amber-300":
              showReconnecting(),
            "text-secondary-text hover:text-primary-text transition-colors": !showReconnecting(),
          }}
        >
          {/* The running-step spinner the activity rows use. */}
          <TbLoader
            class="size-3 shrink-0 animate-spin"
            classList={{
              "text-amber-500": showReconnecting(),
              "text-blue-500 dark:text-blue-400": !showReconnecting(),
            }}
          />
          <span class="min-w-0 truncate">
            <Show when={showReconnecting()} fallback={shown().main}>
              {t("stream_reconnecting_title")}
            </Show>
          </span>
        </button>
        {/* An open timeline shows each agent's card with its live step already. */}
        <Show when={!showReconnecting() && !activityFocus?.isOpen() && shown().agents.length > 0}>
          {/* Ruled like the activity's nested rows: the rule sits under the spinner's centre, and
              the 20px to the text (12px spinner, 8px gap) puts the names under the main line. */}
          <div class="mt-1.5 ml-1.25 border-l-2 border-primary-border pl-3.25">
            <span class="flex min-w-0 flex-col gap-1.5 py-0.5">
              {/* Index, not For: every status rebuilds the line objects, so keying by reference
                  would remount every row whenever any agent's step changed. */}
              <Index each={shown().agents}>
                {(agent) => (
                  <button
                    type="button"
                    aria-label={`${t("heartbeat.open_agent", { name: agent().name })}: ${agent().step}`}
                    onClick={() => activityFocus?.open(agent().focusId)}
                    class="group/agent flex min-w-0 cursor-pointer flex-col rounded text-left focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    <span
                      class="max-w-full truncate text-xs font-medium text-secondary-text group-hover/agent:text-primary-text group-hover/agent:underline"
                      title={agent().name}
                    >
                      {agent().name}
                    </span>
                    <span
                      class="max-w-full truncate pl-2 text-[11px] text-muted-text"
                      classList={{ italic: agent().idle, "opacity-70": agent().done }}
                      title={agent().step}
                    >
                      {agent().step}
                    </span>
                  </button>
                )}
              </Index>
            </span>
          </div>
        </Show>
      </output>
    </Show>
  );
};
